/**
 * @fileoverview Subscribes to sharingInformationService for inbox chat UI side-effects
 * (prefill textarea, focus textarea, open image attach sidebar after drag-and-drop).
 * Keeps ChatComponentMC thin; UI layer only.
 * @module useInboxChatSharingInformationUI
 * @category Hook - UI Layer
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { sharingInformationService } from '../../../../services/sharingInformationService';
import { MAX_IMAGE_SIZE_TRANSFER_WS } from '../../../../constants/chatFileSizes';
import { isTikTokCommentsPlatform } from '../constants/platformMessageRestrictions';

const ATTACH_FILES_SIDEBAR_KEY = 'AttachFilesSidebar';

/**
 * @param {Object} params
 * @param {React.RefObject<HTMLTextAreaElement|HTMLInputElement>} params.textareaRef - Chat input ref.
 * @param {function(Object, string, Object=): void} params.openModal - Sidebar modal opener from useSidebarModalMC.
 * @param {function(string, *): void} params.handleSubmit - Chat attach submit handler (e.g. image type + files).
 * @param {function(boolean): void} params.setOpenAttachMenu - Closes attach popover when opening sidebar.
 */
export default function useInboxChatSharingInformationUI({
  textareaRef,
  openModal,
  handleSubmit,
  setOpenAttachMenu,
  platform,
  flushConsolidatedMessage
}) {
  const { t } = useTranslation('InboxMC');
  const openModalRef = useRef(openModal);
  const handleSubmitRef = useRef(handleSubmit);
  const flushConsolidatedMessageRef = useRef(flushConsolidatedMessage);

  openModalRef.current = openModal;
  handleSubmitRef.current = handleSubmit;
  flushConsolidatedMessageRef.current = flushConsolidatedMessage;

  useEffect(() => {
    const subscription$ = sharingInformationService.getSubject();
    const subscription = subscription$.subscribe((data) => {
      if (data.flushConsolidation && typeof flushConsolidatedMessageRef.current === 'function') {
        flushConsolidatedMessageRef.current();
      }

      if (data.fillInputChatComponent && typeof data.text === 'string') {
        const node = textareaRef.current;
        if (node) {
          node.value = data.text;
          node.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }

      if (data.focusInboxChatTextarea) {
        requestAnimationFrame(() => {
          textareaRef.current?.focus();
        });
      }

      if (data.openInboxImageAttachSidebar && data.inboxImageAttachInitialFiles?.length) {
        const imageMaxFiles = isTikTokCommentsPlatform(platform) ? 1 : 5;
        const initialAttachments = Array.from(data.inboxImageAttachInitialFiles).slice(0, imageMaxFiles);

        setOpenAttachMenu(false);
        openModalRef.current({
          title: t('attachMenu.image'),
          accept: 'image/png,image/jpeg',
          maxFiles: imageMaxFiles,
          maxFileSize: MAX_IMAGE_SIZE_TRANSFER_WS,
          initialAttachments,
          handleSubmit: (attachments) => handleSubmitRef.current('image', attachments)
        }, ATTACH_FILES_SIDEBAR_KEY, { position: 'center' });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [textareaRef, t, setOpenAttachMenu, platform]);
}
